import { useEffect } from 'react'
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation
} from 'react-router-dom'

import styled from 'styled-components'

import { useUser } from 'contexts/user.context'

import About from 'pages/About'
import Admin from 'pages/Admin'
import AdminAds from 'pages/Admin/Ads'
import AdminCommissions from 'pages/Admin/Commissions'
import AdminItineraries from 'pages/Admin/Itineraries'
import AdminLocalities from 'pages/Admin/Localities'
import AdminModeration from 'pages/Admin/Moderation'
import AdminRadar from 'pages/Admin/Radar'
import AdminUsers from 'pages/Admin/Users'
import ChangePassword from 'pages/ChangePassword'
import DataLicense from 'pages/DataLicense'
import Destinations from 'pages/Destinations'
import DestinationSeo from 'pages/DestinationSeo'
import ErrorPage from 'pages/Error'
import Ficha from 'pages/Ficha'
import Home from 'pages/Home'
import CreateItinerary from 'pages/Itinerary/Create'
import ListItinerary from 'pages/Itinerary/List'
import ShowItinerary from 'pages/Itinerary/Show'
import UpdateItinerary from 'pages/Itinerary/Update'
import LoginToken from 'pages/LoginToken'
import Map from 'pages/Map'
import Privacy from 'pages/Privacy'
import Radar from 'pages/Radar'
import RadarNews from 'pages/Radar/News'
import RadarShow from 'pages/Radar/Show'
import Profile from 'pages/Profile'
import RecoveryPassword from 'pages/Recovery'
import Register from 'pages/Register'
import Suggestions from 'pages/Suggestions'
import Terms from 'pages/Terms'
import Update from 'pages/Update'
import User from 'pages/User'
import UserSearch from 'pages/UserSearch'

import AnalyticsTracker from 'components/analyticsTracker'
import Footer from 'components/footer'
import Header from 'components/header'

const PRIVATE_OR_SENSITIVE_PATHS = [
  /^\/admin(?:\/|$)/,
  /^\/minha-conta(?:\/|$)/,
  /^\/editar-perfil(?:\/|$)/,
  /^\/criar-roteiro(?:\/|$)/,
  /^\/editar-roteiro(?:\/|$)/,
  /^\/login-token(?:\/|$)/,
  /^\/mudar-senha(?:\/|$)/,
  /^\/recuperar-senha(?:\/|$)/,
  /^\/buscar-usuarios(?:\/|$)/,
  /(?:^|\/)\.env(?:\.|\/|$)/i,
  /(?:^|\/)\.git(?:\/|$)/i,
  /(?:^|\/)config\/\.env(?:\.|\/|$)/i
]

const SeoRouteGuard = () => {
  const location = useLocation()

  useEffect(() => {
    const hostname = window.location.hostname.toLowerCase()
    const pathname = location.pathname || '/'
    const search = location.search || ''
    const isRailwayHost = hostname.endsWith('.up.railway.app')
    const shouldNoIndex =
      isRailwayHost ||
      PRIVATE_OR_SENSITIVE_PATHS.some((pattern) => pattern.test(pathname))

    let canonical = document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null

    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }

    canonical.href = `https://redsterna.com.br${pathname}`

    let robots = document.head.querySelector(
      'meta[name="robots"]'
    ) as HTMLMetaElement | null

    if (!robots) {
      robots = document.createElement('meta')
      robots.name = 'robots'
      document.head.appendChild(robots)
    }

    robots.content = shouldNoIndex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large'

    if (search && shouldNoIndex) {
      canonical.href = `https://redsterna.com.br${pathname}`
    }
  }, [location.pathname, location.search])

  return null
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  min-height: 100vh;
  height: auto;
  width: 100%;
  background-color: #ffff;
`

const Router = () => {
  const { user } = useUser()

  const authenticated = Boolean(user || window.localStorage.getItem('token'))

  return (
    <BrowserRouter>
      <SeoRouteGuard />
      <AnalyticsTracker />
      <Switch>
        <Route exact path="/admin/usuarios">
          {authenticated && user?.isAdmin ? (
            <AdminUsers />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/roteiros">
          {authenticated && user?.isAdmin ? (
            <AdminItineraries />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/localidades">
          {authenticated && user?.isAdmin ? (
            <AdminLocalities />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/moderacao">
          {authenticated && user?.isAdmin ? (
            <AdminModeration />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/anuncios">
          {authenticated && (user?.isAdmin || user?.isCommercial) ? (
            <AdminAds />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/comissoes">
          {authenticated && (user?.isAdmin || user?.isCommercial) ? (
            <AdminCommissions />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin/radar">
          {authenticated && (user?.isAdmin || user?.isPublisher) ? (
            <AdminRadar />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>
        <Route exact path="/admin">
          {authenticated && user?.isAdmin ? (
            <Admin />
          ) : authenticated && user?.isPublisher ? (
            <Redirect to="/admin/radar" />
          ) : authenticated && user?.isCommercial ? (
            <Redirect to="/admin/anuncios" />
          ) : (
            <Redirect to={authenticated ? '/minha-conta' : '/'} />
          )}
        </Route>

        <Route>
          <Wrapper>
            <Header authenticated={authenticated} />
            <Switch>
              <Route exact path="/" component={Home} />
              <Route exact path="/mudar-senha" component={ChangePassword} />
              <Route
                exact
                path="/recuperar-senha"
                component={RecoveryPassword}
              />
              <Route exact path="/destinos" component={Destinations} />
              <Route exact path="/destinos/:slug" component={DestinationSeo} />
              <Route exact path="/roteiros" component={ListItinerary} />
              <Route
                exact
                path="/roteiros/:destinations/:shortId"
                component={ShowItinerary}
              />
              <Route exact path="/roteiro/:id" component={ShowItinerary} />
              <Route exact path="/radar" component={Radar} />
              <Route exact path="/radar/noticias" component={RadarNews} />
              <Route exact path="/radar/:slug" component={RadarShow} />
              <Route exact path="/ficha/:city" component={Ficha} />

              <Route exact path="/dicas-de-viagem" component={Suggestions} />
              <Route exact path="/dicas-de-viagem/:name">
                <Redirect to="/dicas-de-viagem" />
              </Route>
              <Route exact path="/mapa" component={Map} />
              <Route exact path="/cadastre-se" component={Register} />
              <Route exact path="/usuario/:id" component={User} />
              <Route exact path="/buscar-usuarios" component={UserSearch} />
              <Route
                exact
                path="/politicas-privacidade"
                render={() => <Redirect to="/politica-de-privacidade" />}
              />
              <Route
                exact
                path="/politica-de-privacidade"
                component={Privacy}
              />
              <Route exact path="/termos-de-uso" component={Terms} />
              <Route exact path="/licenca-de-dados" component={DataLicense} />
              <Route
                exact
                path="/licenca-de-dados/v1"
                component={DataLicense}
              />
              <Route exact path="/quem-somos" component={About} />
              <Route exact path="/login-token" component={LoginToken} />

              {authenticated && (
                <>
                  <Route exact path="/minha-conta" component={Profile} />
                  <Route exact path="/editar-perfil" component={Update} />
                  <Route
                    exact
                    path="/criar-roteiro"
                    component={CreateItinerary}
                  />
                  <Route
                    exact
                    path="/editar-roteiro/:id"
                    component={UpdateItinerary}
                  />
                </>
              )}
              <Route exact path="*" component={ErrorPage} />
              <Route exact path="/error" component={ErrorPage} />
            </Switch>
            <Footer />
          </Wrapper>
        </Route>
      </Switch>
    </BrowserRouter>
  )
}

export default Router
